import { useState, useEffect } from 'react';

/**
 * Custom hook to determine if a component should be rendered
 * based on a target removal date AND time in New York timezone.
 *
 * @param {string} dateRemove - A date string in YYYY-MM-DD format
 * @param {string} timeRemove - A time string in HH:MM 24-hour format (e.g., "14:30")
 * @returns {boolean} - Whether the component should be shown
 */
const useShouldRender = (dateRemove, timeRemove) => {
  const [shouldRender, setShouldRender] = useState(true);

  useEffect(() => {
    if (!dateRemove || !timeRemove) {
      setShouldRender(true);
      return;
    }

    const NY_TIMEZONE = 'America/New_York';

    // Get current date and time in New York timezone
    const now = new Date();

    const nyDateStr = now.toLocaleDateString('en-CA', { timeZone: NY_TIMEZONE }); // "YYYY-MM-DD"
    const nyTimeStr = now.toLocaleTimeString('en-GB', {
      timeZone: NY_TIMEZONE,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }); // "HH:MM"

    // Don't render if target date is in the past
    if (dateRemove < nyDateStr) {
      setShouldRender(false);
      return;
    }

    // Don't render if target date is today (NY) but current NY time has passed target time
    if (dateRemove === nyDateStr && nyTimeStr >= timeRemove) {
      setShouldRender(false);
      return;
    }

    setShouldRender(true);
  }, [dateRemove, timeRemove]);

  return shouldRender;
};

export default useShouldRender;